import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import dayjs from 'dayjs';
import { EmployeeCalendarRegisterRecurrenceAttributesConfig } from 'src/app/models/employee_calendar_register.model';
import { UtilsService } from 'src/app/services';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-calendar-event-detail-recurrence-customization-dialog',
  templateUrl: './calendar-event-detail-recurrence-customization-dialog.component.html',
  styleUrl: './calendar-event-detail-recurrence-customization-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatRadioGroup,
    MatRadioButton,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    MatButton,
    MatIcon,
    DatePipe,
  ],
})
export class CalendarEventDetailRecurrenceCustomizationDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<CalendarEventDetailRecurrenceCustomizationDialogComponent>>(MatDialogRef);
  data = inject<{
    recurrence_configuration: EmployeeCalendarRegisterRecurrenceAttributesConfig | undefined;
    current_day: Date;
  }>(MAT_DIALOG_DATA);
  protected fb = inject(FormBuilder);
  utils = inject(UtilsService);

  form: FormGroup;
  form_monthly_freq_type_ctrl: FormControl<'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week'> = new FormControl();
  form_ending_type_ctrl: FormControl<'until' | 'count' | ''> = new FormControl<'until' | 'count' | ''>('') as FormControl<
    'until' | 'count' | ''
  >;

  constructor() {
    this.form = this.fb.group({
      recurrence_freq: ['weekly', Validators.compose([Validators.required])],
      recurrence_interval: [1, Validators.compose([])],
      recurrence_ending_count: ['', Validators.compose([])],
      recurrence_ending_until: ['', Validators.compose([])],
      recurrence_byday: ['', Validators.compose([])],
      recurrence_bymonthday: ['', Validators.compose([])],
      recurrence_byyearday: ['', Validators.compose([])],
      recurrence_byweekno: ['', Validators.compose([])],
      recurrence_bymonth: ['', Validators.compose([])],
      recurrence_bysetpos: ['', Validators.compose([])],
    });

    if (this.data.recurrence_configuration) {
      const normalized_recurrence_configuration = this.normalizeRecurrenceConfiguration(this.data.recurrence_configuration);
      this.form.patchValue(normalized_recurrence_configuration);
      this.initMonthlyFreqTypeCtrl();
      this.initEndingTypeCtrl();
      this.refreshValidators();
    }
  }

  ngOnInit(): void {
    this.listenMonthlyFreqTypeCtrl();
    this.listenEndingTypeCtrl();
    this.listenFreqCtrl();
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.dialogRef.close(this.form.value);
  }

  private initMonthlyFreqTypeCtrl() {
    if (this.form.controls['recurrence_freq'].value === 'monthly') {
      if (this.form.controls['recurrence_bysetpos'].value != '' && this.form.controls['recurrence_bysetpos'].value != null) {
        const recurrence_bysetpos = this.form.controls['recurrence_bysetpos'].value as number[];
        if (Array.isArray(recurrence_bysetpos) && recurrence_bysetpos.length > 0 && recurrence_bysetpos[0] < 0) {
          this.form_monthly_freq_type_ctrl.patchValue('monthly-on-back-week');
        } else {
          this.form_monthly_freq_type_ctrl.patchValue('monthly-on-nth-week');
        }
      } else if (this.form.controls['recurrence_byweekno'].value != '' && this.form.controls['recurrence_byweekno'].value != null) {
        this.form.patchValue({
          recurrence_bysetpos: this.form.controls['recurrence_byweekno'].value,
          recurrence_byweekno: '',
        });
        this.form_monthly_freq_type_ctrl.patchValue('monthly-on-nth-week');
      } else {
        this.form_monthly_freq_type_ctrl.patchValue('monthly-on-day');
      }
    } else this.form_monthly_freq_type_ctrl.patchValue('monthly-on-day');
  }

  private initEndingTypeCtrl() {
    if (this.form.controls['recurrence_ending_count'].value != '' && this.form.controls['recurrence_ending_count'].value != null) {
      this.form_ending_type_ctrl.patchValue('count');
    } else if (this.form.controls['recurrence_ending_until'].value != '' && this.form.controls['recurrence_ending_until'].value != null) {
      this.form_ending_type_ctrl.patchValue('until');
    }
  }

  private listenMonthlyFreqTypeCtrl() {
    this.form_monthly_freq_type_ctrl.valueChanges.subscribe((data: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => {
      const current_weekday = dayjs(this.data.current_day).isoWeekday();
      if (data === 'monthly-on-day') {
        this.form.patchValue({
          recurrence_byday: '',
          recurrence_bymonthday: [dayjs(this.data.current_day).date()],
          recurrence_byweekno: '',
          recurrence_bysetpos: '',
        });
      } else if (data === 'monthly-on-nth-week') {
        this.form.patchValue({
          recurrence_byday: [current_weekday],
          recurrence_bymonthday: undefined,
          recurrence_byweekno: '',
          recurrence_bysetpos: [this.utils.getWeekNumberOfMonth(this.data.current_day)],
        });
      } else if (data === 'monthly-on-back-week') {
        this.form.patchValue({
          recurrence_byday: [current_weekday],
          recurrence_bymonthday: undefined,
          recurrence_byweekno: '',
          recurrence_bysetpos: [this.getWeekPositionFromEndOfMonth()],
        });
      }

      this.refreshValidators();
    });
  }

  private listenEndingTypeCtrl() {
    this.form_ending_type_ctrl.valueChanges.subscribe((data: 'until' | 'count' | '') => {
      if (data === 'until') {
        this.form.patchValue({
          recurrence_ending_count: '',
          recurrence_ending_until: dayjs(this.data.current_day).add(1, 'month').toDate(),
        });
      } else if (data === 'count') {
        this.form.patchValue({
          recurrence_ending_count: 10,
          recurrence_ending_until: '',
        });
      } else if (data === '') {
        this.form.patchValue({
          recurrence_ending_count: '',
          recurrence_ending_until: '',
        });
      }
    });
  }

  private listenFreqCtrl() {
    this.form.controls['recurrence_freq'].valueChanges.subscribe((data: 'daily' | 'weekly' | 'monthly' | 'yearly') => {
      this.onRecurrenceFreqTypeUpdated(data);
    });
  }

  private onRecurrenceFreqTypeUpdated(input: 'daily' | 'weekly' | 'monthly' | 'yearly' | undefined = undefined) {
    const body = this.getNormalizedRecurrenceFields(input);
    this.form.patchValue(body);

    // 2. Activem validadors
    this.refreshValidators();
  }

  private getNormalizedRecurrenceFields(
    input: 'daily' | 'weekly' | 'monthly' | 'yearly' | undefined = undefined
  ): Partial<EmployeeCalendarRegisterRecurrenceAttributesConfig> {
    const data: 'daily' | 'weekly' | 'monthly' | 'yearly' = input ?? this.form.value.recurrence_freq;
    const current_weekday = dayjs(this.data.current_day).isoWeekday();

    let recurrence_byday: number[] | undefined = undefined;
    let recurrence_bymonthday: number[] | undefined = undefined;
    let recurrence_byweekno: number[] | undefined = undefined;
    let recurrence_bysetpos: number[] | undefined = undefined;
    let recurrence_byyearday: number[] | undefined = undefined;

    if (data === 'weekly') {
      recurrence_byday = [current_weekday];
    } else if (data === 'monthly') {
      if (this.form_monthly_freq_type_ctrl.value === 'monthly-on-day') {
        recurrence_bymonthday = [dayjs(this.data.current_day).date()];
      } else if (this.form_monthly_freq_type_ctrl.value === 'monthly-on-nth-week') {
        recurrence_byday = [current_weekday];
        recurrence_bysetpos = [this.utils.getWeekNumberOfMonth(this.data.current_day)];
      } else if (this.form_monthly_freq_type_ctrl.value === 'monthly-on-back-week') {
        recurrence_byday = [current_weekday];
        recurrence_bysetpos = [this.getWeekPositionFromEndOfMonth()];
      }
    } else if (data === 'yearly') {
      recurrence_byyearday = [dayjs(this.data.current_day).dayOfYear()];
    }

    return {
      recurrence_byday: recurrence_byday,
      recurrence_bymonthday: recurrence_bymonthday,
      recurrence_byyearday: recurrence_byyearday,
      recurrence_byweekno: recurrence_byweekno,
      recurrence_bymonth: undefined,
      recurrence_bysetpos: recurrence_bysetpos,
    };
  }

  private refreshValidators() {
    const data: 'daily' | 'weekly' | 'monthly' | 'yearly' = this.form.controls['recurrence_freq'].value;

    for (const form_key of Object.keys(VALIDATORS_CONFIG[data]) as (keyof ValidatorsConfigRow)[]) {
      const validatorConfig = VALIDATORS_CONFIG[data][form_key];
      // Si la propiedad es un array de ValidatorFn[]
      if (Array.isArray(validatorConfig)) {
        this.form.controls[form_key].setValidators(validatorConfig);
      } else if (typeof validatorConfig === 'function') {
        // Si es una función, puedes llamarla con un argumento
        const validators = validatorConfig(this.form_monthly_freq_type_ctrl.value);
        this.form.controls[form_key].setValidators(validators);
      }

      this.form.controls[form_key].updateValueAndValidity();
    }
  }

  getCurrentMonthWeekLabel(): string {
    const week_number = this.utils.getWeekNumberOfMonth(this.data.current_day);
    const week_number_map: { [key: number]: string } = {
      1: 'primer',
      2: 'segundo',
      3: 'tercer',
      4: 'cuarto',
      5: 'quinto',
    };

    return week_number_map[week_number] ?? `${week_number}o`;
  }

  getCurrentMonthBackWeekLabel(): string {
    const bysetpos_from_end = this.getWeekPositionFromEndOfMonth();
    const back_week_map: { [key: number]: string } = {
      [-1]: 'último',
      [-2]: 'penúltimo',
      [-3]: 'antepenúltimo',
      [-4]: 'cuarto por el final',
      [-5]: 'quinto por el final',
    };

    return back_week_map[bysetpos_from_end] ?? `${Math.abs(bysetpos_from_end)}o por el final`;
  }

  private getWeekPositionFromEndOfMonth(): number {
    const current_day = dayjs(this.data.current_day);
    const weekday = current_day.isoWeekday();
    let cursor = current_day.endOf('month');
    let occurrences_from_end = 0;

    while (cursor.month() === current_day.month()) {
      if (cursor.isoWeekday() === weekday) {
        occurrences_from_end++;
        if (cursor.isSame(current_day, 'day')) {
          return -occurrences_from_end;
        }
      }
      cursor = cursor.subtract(1, 'day');
    }

    return -1;
  }

  private normalizeRecurrenceConfiguration(
    recurrence_configuration: EmployeeCalendarRegisterRecurrenceAttributesConfig
  ): EmployeeCalendarRegisterRecurrenceAttributesConfig {
    const normalized_recurrence_configuration = { ...recurrence_configuration };

    if (normalized_recurrence_configuration.recurrence_freq == null) {
      normalized_recurrence_configuration.recurrence_freq = 'weekly';
    }
    if (normalized_recurrence_configuration.recurrence_interval == null) {
      normalized_recurrence_configuration.recurrence_interval = 1;
    }

    const is_weekly_without_days =
      normalized_recurrence_configuration.recurrence_freq === 'weekly' &&
      (!Array.isArray(normalized_recurrence_configuration.recurrence_byday) ||
        normalized_recurrence_configuration.recurrence_byday.length === 0);
    if (is_weekly_without_days) {
      normalized_recurrence_configuration.recurrence_byday = [dayjs(this.data.current_day).isoWeekday()];
    }

    return normalized_recurrence_configuration;
  }
}

interface ValidatorsConfig {
  daily: ValidatorsConfigRow;
  weekly: ValidatorsConfigRow;
  monthly: ValidatorsConfigRow;
  yearly: ValidatorsConfigRow;
}

interface ValidatorsConfigRow {
  recurrence_byday: ValidatorFn[];
  recurrence_bymonthday: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => ValidatorFn[];
  recurrence_byyearday: ValidatorFn[];
  recurrence_byweekno: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => ValidatorFn[];
  recurrence_bymonth: ValidatorFn[];
  recurrence_bysetpos: ValidatorFn[];
}

const VALIDATORS_CONFIG: ValidatorsConfig = {
  daily: {
    recurrence_byday: [],
    recurrence_bymonthday: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_byyearday: [],
    recurrence_byweekno: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_bymonth: [],
    recurrence_bysetpos: [],
  },
  weekly: {
    recurrence_byday: [],
    recurrence_bymonthday: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_byyearday: [],
    recurrence_byweekno: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_bymonth: [],
    recurrence_bysetpos: [],
  },
  monthly: {
    recurrence_byday: [],
    recurrence_bymonthday: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') =>
      type === 'monthly-on-day' ? [Validators.required] : [],
    recurrence_byyearday: [],
    recurrence_byweekno: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_bymonth: [],
    recurrence_bysetpos: [],
  },
  yearly: {
    recurrence_byday: [],
    recurrence_bymonthday: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_byyearday: [],
    recurrence_byweekno: (type: 'monthly-on-day' | 'monthly-on-nth-week' | 'monthly-on-back-week') => [],
    recurrence_bymonth: [],
    recurrence_bysetpos: [],
  },
};
